"use client"

import { useCallback, useEffect, useMemo, useRef, useState, type SyntheticEvent } from 'react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Progress } from '@/components/ui/progress'
import { Pause, Play, SkipForward, SkipBack, Loader2 } from 'lucide-react'
import type { StoryboardSegmentPreviewEntry } from '../types';
import useStoryboardMediaSource from '../hooks/useStoryboardMediaSource';

type VideoFrameCallbackMetadata = {
  mediaTime?: number;
  presentedFrames?: number;
};

type HTMLVideoElementWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    callback: (now: number, metadata: VideoFrameCallbackMetadata) => void
  ) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

interface PreviewFramePickerDialogProps {
  open: boolean;
  entry: StoryboardSegmentPreviewEntry | null;
  projectId: string | null;
  segmentId: string | null;
  onClose: () => void;
  onSelect: (dataUrl: string) => Promise<void> | void;
}

const PreviewFramePickerDialog: React.FC<PreviewFramePickerDialogProps> = ({
  open,
  entry,
  projectId,
  segmentId,
  onClose,
  onSelect,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [frameRate, setFrameRate] = useState<number>(30);
  const [, setCurrentFrame] = useState(0);
  const [sliderFrame, setSliderFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const draggingRef = useRef(false);
  const frameRateRef = useRef(frameRate);
  const durationRef = useRef<number | null>(videoDuration);

  const { src, loading, error } = useStoryboardMediaSource({
    projectId,
    segmentId,
    entry,
    autoFetch: open,
  });

  const effectiveSrc = useMemo(() => src ?? entry?.url ?? undefined, [src, entry?.url]);
  frameRateRef.current = frameRate;

  useEffect(() => {
    setVideoReady(false);
    setCaptureError(null);
    setCapturing(false);
    setVideoDuration(null);
    setCurrentFrame(0);
    setSliderFrame(0);
    setIsPlaying(false);
  }, [open, effectiveSrc]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const node = videoRef.current;
    if (!node) {
      return;
    }

    const updateCurrentFrame = (timeInSeconds: number) => {
      if (!Number.isFinite(timeInSeconds)) {
        return;
      }
      const fps = frameRateRef.current;
      if (!Number.isFinite(fps) || fps <= 0) {
        return;
      }
      const nextFrame = Math.max(Math.round(timeInSeconds * fps), 0);
      setCurrentFrame(nextFrame);
      if (!draggingRef.current) {
        setSliderFrame(nextFrame);
      }
    };

    const handleLoadedMetadata = () => {
      if (!node) {
        return;
      }
      const durationValue =
        Number.isFinite(node.duration) && node.duration > 0 ? node.duration : null;
      setVideoDuration(durationValue);
      updateCurrentFrame(node.currentTime);
    };

    const handleLoadedData = () => {
      setVideoReady(true);
      setCaptureError(null);
    };

    const handleError = () => {
      setVideoReady(false);
      setCaptureError('视频加载失败，请稍后再试。');
    };

    const handleTimeUpdate = () => updateCurrentFrame(node.currentTime);
    const handleSeeked = () => updateCurrentFrame(node.currentTime);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    node.addEventListener('loadeddata', handleLoadedData);
    node.addEventListener('loadedmetadata', handleLoadedMetadata);
    node.addEventListener('error', handleError);
    node.addEventListener('timeupdate', handleTimeUpdate);
    node.addEventListener('seeked', handleSeeked);
    node.addEventListener('play', handlePlay);
    node.addEventListener('pause', handlePause);

    if (node.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && node.videoWidth > 0) {
      handleLoadedData();
    }
    if (node.readyState >= HTMLMediaElement.HAVE_METADATA) {
      handleLoadedMetadata();
    }

    return () => {
      node.removeEventListener('loadeddata', handleLoadedData);
      node.removeEventListener('loadedmetadata', handleLoadedMetadata);
      node.removeEventListener('error', handleError);
      node.removeEventListener('timeupdate', handleTimeUpdate);
      node.removeEventListener('seeked', handleSeeked);
      node.removeEventListener('play', handlePlay);
      node.removeEventListener('pause', handlePause);
    };
  }, [open, effectiveSrc]);

  useEffect(() => {
    const node = videoRef.current as HTMLVideoElementWithFrameCallback | null;
    if (!open || !node || typeof node.requestVideoFrameCallback !== 'function') {
      return;
    }

    let cancelled = false;

    const frameCallback = (_now: number, metadata: VideoFrameCallbackMetadata) => {
      if (cancelled) return;
      if (typeof metadata.mediaTime === 'number' && Number.isFinite(metadata.mediaTime)) {
        const fps = frameRateRef.current;
        if (Number.isFinite(fps) && fps > 0) {
          const nextFrame = Math.max(Math.round(metadata.mediaTime * fps), 0);
          setCurrentFrame(nextFrame);
          if (!draggingRef.current) {
            setSliderFrame(nextFrame);
          }
        }
      }
      if (
        typeof metadata.presentedFrames === 'number' &&
        metadata.presentedFrames > 1 &&
        typeof metadata.mediaTime === 'number' &&
        metadata.mediaTime > 0.2
      ) {
        const candidate = metadata.presentedFrames / metadata.mediaTime;
        if (Number.isFinite(candidate) && candidate >= 5 && candidate <= 120) {
          setFrameRate((prev) => {
            const blended = prev * 0.5 + candidate * 0.5;
            const rounded = Math.round(blended * 100) / 100;
            return rounded <= 0 ? prev : rounded;
          });
        }
      }
      if (!cancelled && typeof node.requestVideoFrameCallback === 'function') {
        node.requestVideoFrameCallback(frameCallback);
      }
    };

    const handle = node.requestVideoFrameCallback(frameCallback);

    return () => {
      cancelled = true;
      if (typeof node.cancelVideoFrameCallback === 'function') {
        node.cancelVideoFrameCallback(handle);
      }
    };
  }, [open, effectiveSrc]);

  useEffect(() => {
    const node = videoRef.current;
    if (!node) {
      return;
    }
    if (!Number.isFinite(node.currentTime)) {
      return;
    }
    const fps = frameRateRef.current;
    if (!Number.isFinite(fps) || fps <= 0) {
      return;
    }
    const nextFrame = Math.max(Math.round(node.currentTime * fps), 0);
    setCurrentFrame(nextFrame);
    if (!draggingRef.current) {
      setSliderFrame(nextFrame);
    }
  }, [frameRate]);

  const handleConfirm = async () => {
    const element = videoRef.current;
    if (!element) {
      setCaptureError('视频尚未加载，无法截取画面。');
      return;
    }
    if (!element.videoWidth || !element.videoHeight) {
      setCaptureError('视频尚未准备好，请稍后再试。');
      return;
    }
    setCapturing(true);
    setCaptureError(null);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = element.videoWidth;
      canvas.height = element.videoHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('无法创建画布上下文。');
      }
      context.drawImage(element, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      await onSelect(dataUrl);
    } catch (err) {
      console.error('[storyboard] 捕获参考帧失败', err);
      const message =
        err instanceof Error && err.message ? err.message : '截取画面失败，请稍后再试。';
      setCaptureError(message);
    } finally {
      setCapturing(false);
    }
  };

  const resolvedDuration = useMemo(() => {
    if (videoDuration && Number.isFinite(videoDuration) && videoDuration > 0) {
      return videoDuration;
    }
    return null;
  }, [videoDuration]);

  useEffect(() => {
    durationRef.current = resolvedDuration;
  }, [resolvedDuration]);

  const maxFrame = useMemo(() => {
    if (!resolvedDuration) {
      return 0;
    }
    const fps = frameRateRef.current;
    if (!Number.isFinite(fps) || fps <= 0) {
      return 0;
    }
    return Math.max(Math.round(resolvedDuration * fps), 0);
  }, [resolvedDuration]);

  useEffect(() => {
    if (maxFrame <= 0) {
      setCurrentFrame(0);
      setSliderFrame(0);
      return;
    }
    setCurrentFrame((prev) => Math.max(Math.min(prev, maxFrame), 0));
    setSliderFrame((prev) => Math.max(Math.min(prev, maxFrame), 0));
  }, [maxFrame]);

  const formatTimecode = useCallback(
    (frame: number): string => {
      const fps = frameRateRef.current;
      if (!Number.isFinite(fps) || fps <= 0) {
        return '00:00.00';
      }
      const seconds = Math.max(frame, 0) / fps;
      const totalMilliseconds = Math.round(seconds * 1000);
      const totalSeconds = Math.floor(totalMilliseconds / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      const milliseconds = Math.floor((totalMilliseconds % 1000) / 10);
      const mm = minutes.toString().padStart(2, '0');
      const ss = secs.toString().padStart(2, '0');
      const ms = milliseconds.toString().padStart(2, '0');
      return `${mm}:${ss}.${ms}`;
    },
    []
  );

  const seekToFrame = useCallback(
    (rawFrame: number) => {
      const element = videoRef.current;
      const fps = frameRateRef.current;
      if (!element || !Number.isFinite(fps) || fps <= 0) {
        return;
      }
      const frame = Math.max(Math.min(rawFrame, maxFrame), 0);
      const epsilon = 1 / fps;
      const duration = durationRef.current;
      const targetTime = frame / fps;
      const boundary =
        duration && Number.isFinite(duration) && duration > 0
          ? Math.max(duration - epsilon, 0)
          : undefined;
      const clampedTime = boundary !== undefined ? Math.min(targetTime, boundary) : targetTime;

      const handleSeeked = () => {
        element.removeEventListener('seeked', handleSeeked);
      };
      element.pause();
      element.addEventListener('seeked', handleSeeked);
      try {
        element.currentTime = clampedTime;
      } catch (err) {
        element.removeEventListener('seeked', handleSeeked);
        console.error('[storyboard] 设置帧位置失败', err);
      }
    },
    [maxFrame]
  );

  const handleSliderChange = (_event: Event | SyntheticEvent, value: number | number[]) => {
    const frame = Array.isArray(value) ? value[0] : value;
    const normalized = Math.max(Math.min(frame, maxFrame), 0);
    draggingRef.current = true;
    setSliderFrame(normalized);
  };

  const handleSliderChangeCommitted = (
    _event: Event | SyntheticEvent,
    value: number | number[]
  ) => {
    const frame = Array.isArray(value) ? value[0] : value;
    const normalized = Math.max(Math.min(frame, maxFrame), 0);
    draggingRef.current = false;
    setSliderFrame(normalized);
    setCurrentFrame(normalized);
    seekToFrame(normalized);
  };

  const handleStepFrame = (offset: number) => {
    setSliderFrame((prev) => {
      const next = Math.max(Math.min(prev + offset, maxFrame), 0);
      setCurrentFrame(next);
      seekToFrame(next);
      return next;
    });
  };

  const togglePlayback = async () => {
    const element = videoRef.current;
    if (!element) return;
    try {
      if (element.paused || element.ended) {
        await element.play();
      } else {
        element.pause();
      }
    } catch (err) {
      console.error('[storyboard] 无法切换播放状态', err);
    }
  };

  const canUseFrameControls = resolvedDuration !== null && maxFrame > 0;
  const clampedSliderFrame = Math.min(sliderFrame, maxFrame);
  const totalFrames = maxFrame + 1;

  const canConfirm = Boolean(effectiveSrc) && videoReady && !capturing;

  return (
    <Dialog open={open} onOpenChange={(v) => (!capturing && !v ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>选择参考帧</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">
            使用帧序列滑块精确定位到目标画面，再点击「使用当前帧」设置为下一个单元的参考图。
          </div>
          {effectiveSrc ? (
            <video
              ref={videoRef}
              src={effectiveSrc}
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
              style={{ width: '100%', maxHeight: 420, borderRadius: 12, backgroundColor: '#000' }}
            />
          ) : (
            <div className="text-sm text-muted-foreground">暂无可用视频源，无法截取参考帧。</div>
          )}
          {canUseFrameControls ? (
            <div className="space-y-2">
              <Slider
                value={[clampedSliderFrame]}
                min={0}
                max={Math.max(maxFrame, 1)}
                step={1}
                onValueChange={(v) => setSliderFrame(Array.isArray(v) ? (v[0] ?? 0) : (v as number))}
                // @ts-expect-error radix prop passthrough
                onValueCommit={(v: number[]) => handleSliderChangeCommitted(v[0] ?? 0)}
                disabled={!videoReady || capturing}
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div>帧 {totalFrames > 0 ? clampedSliderFrame + 1 : 0} / {Math.max(totalFrames, 1)}</div>
                <div>时间 {formatTimecode(clampedSliderFrame)}</div>
                <div>帧率 {frameRate.toFixed(2)} fps</div>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleStepFrame(-1)} disabled={!videoReady || capturing || clampedSliderFrame <= 0}>
                  <SkipBack className="size-4 mr-1" /> 上一帧
                </Button>
                <Button size="sm" onClick={togglePlayback} disabled={!videoReady || capturing}>
                  {isPlaying ? (
                    <>
                      <Pause className="size-4 mr-1" /> 暂停
                    </>
                  ) : (
                    <>
                      <Play className="size-4 mr-1" /> 播放
                    </>
                  )}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleStepFrame(1)} disabled={!videoReady || capturing || clampedSliderFrame >= maxFrame}>
                  <SkipForward className="size-4 mr-1" /> 下一帧
                </Button>
              </div>
            </div>
          ) : null}
          {(loading || !videoReady) && effectiveSrc ? (
            <div className="flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              <Progress value={0} className="flex-1" />
            </div>
          ) : null}
          {error ? <div className="text-sm text-destructive">{error.message}</div> : null}
          {captureError ? <div className="text-sm text-destructive">{captureError}</div> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={capturing}>
            取消
          </Button>
          <Button onClick={() => void handleConfirm()} disabled={!canConfirm}>
            {capturing ? '处理中…' : '使用当前帧'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
};

export default PreviewFramePickerDialog;
